import { normalizePedagogicalPattern } from './patternVocabulary';

export const PEDAGOGICAL_VARIANTS = [
  'graph',
  'table',
  'timeline',
  'tree',
  'stack_queue',
  'coordinate_plane',
  'flow_diagram',
  'set_diagram',
  'heatmap',
  'field',
  'shapes',
  'character_scene',
  'chart',
  'chat',
];

const PEDAGOGICAL_VARIANT_SET = new Set(PEDAGOGICAL_VARIANTS);

const VARIANT_REQUIRED_FIELD = {
  graph: 'graph',
  table: 'table',
  timeline: 'timeline',
  tree: 'tree',
  stack_queue: 'stackQueue',
  coordinate_plane: 'coordinatePlane',
  flow_diagram: 'flowDiagram',
  set_diagram: 'setDiagram',
  heatmap: 'heatmap',
  field: 'field',
  shapes: 'shapes',
  character_scene: 'characters',
  chart: 'chart',
  chat: 'chat',
};

function isObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function makeDiagnostic({
  severity = 'warn',
  code,
  message,
  nodeId = null,
  carrierKind = null,
  path = '',
}) {
  return {
    severity,
    code,
    message,
    nodeId,
    carrierKind,
    path,
  };
}

export function validatePedagogicalDiagramPayload(payload, context = {}) {
  const diagnostics = [];
  const nodeId = context.nodeId || null;
  const carrierKind = context.carrierKind || null;
  const path = context.path || '';

  if (!isObject(payload)) {
    diagnostics.push(
      makeDiagnostic({
        severity: 'error',
        code: 'PED_DIAGRAM_PAYLOAD_INVALID',
        message: 'PEDAGOGICAL_DIAGRAM payload must be an object.',
        nodeId,
        carrierKind,
        path,
      })
    );
    return diagnostics;
  }

  const variantRaw = String(payload.variant || payload.type || '')
    .trim()
    .toLowerCase();
  if (!variantRaw || !PEDAGOGICAL_VARIANT_SET.has(variantRaw)) {
    diagnostics.push(
      makeDiagnostic({
        severity: 'error',
        code: 'PED_DIAGRAM_VARIANT_INVALID',
        message: `Unsupported pedagogical diagram variant: "${variantRaw || 'missing'}".`,
        nodeId,
        carrierKind,
        path,
      })
    );
  } else {
    const requiredField = VARIANT_REQUIRED_FIELD[variantRaw];
    if (requiredField) {
      const requiredValue = payload[requiredField];
      const validByType =
        Array.isArray(requiredValue) || (requiredValue && typeof requiredValue === 'object');
      if (!validByType) {
        diagnostics.push(
          makeDiagnostic({
            severity: 'warn',
            code: 'PED_DIAGRAM_VARIANT_PAYLOAD_MISSING',
            message: `Variant "${variantRaw}" expects field "${requiredField}" with structured data.`,
            nodeId,
            carrierKind,
            path,
          })
        );
      }
    }
  }

  const rawPattern = String(payload.pattern || '').trim();
  if (rawPattern) {
    const normalizedPattern = normalizePedagogicalPattern(rawPattern);
    if (!normalizedPattern) {
      diagnostics.push(
        makeDiagnostic({
          severity: 'warn',
          code: 'PED_DIAGRAM_PATTERN_INVALID',
          message: `Unsupported pedagogical pattern: "${rawPattern}".`,
          nodeId,
          carrierKind,
          path,
        })
      );
    }
  }

  return diagnostics;
}

export function collectPedagogicalContractDiagnostics(lessonNodes = []) {
  const diagnostics = [];
  const nodes = Array.isArray(lessonNodes) ? lessonNodes : [];

  const visit = (value, context) => {
    if (Array.isArray(value)) {
      value.forEach((item, idx) => visit(item, { ...context, path: `${context.path}[${idx}]` }));
      return;
    }

    if (!isObject(value)) return;

    const nextNodeId = value.id ? String(value.id) : context.nodeId;
    const nextKind = value.kind ? String(value.kind) : context.carrierKind;
    const nextPath = context.path || '';

    if (nextKind === 'PEDAGOGICAL_DIAGRAM') {
      diagnostics.push(
        ...validatePedagogicalDiagramPayload(value.payload, {
          nodeId: nextNodeId,
          carrierKind: context.carrierKind || 'PEDAGOGICAL_DIAGRAM',
          path: `${nextPath}.payload`,
        })
      );
      return;
    }

    Object.entries(value).forEach(([key, child]) => {
      visit(child, {
        nodeId: nextNodeId,
        carrierKind: nextKind,
        path: `${nextPath}.${key}`,
      });
    });
  };

  nodes.forEach((node, idx) => {
    visit(node, {
      nodeId: node?.id ? String(node.id) : null,
      carrierKind: node?.kind ? String(node.kind) : null,
      path: `$[${idx}]`,
    });
  });

  return diagnostics;
}
